import { Tooltip, Typography } from '@material-ui/core';
import {
  AccessTime,
  HourglassEmpty,
  Info,
  WarningRounded,
} from '@material-ui/icons';
import { CheckIcon } from 'shared/icons/CheckIcon';
import { FormattedDate } from '@superdispatch/dates';
import {
  Color,
  ColorDynamic,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { BankErrorMessage } from 'shared/errors/SuperPayBankErrors';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { IconTag } from 'shared/ui/IconTag';
import styled from 'styled-components';
import { LoadPaymentDTO } from '../data/LoadDTO';
import {
  InstantTransferStatus,
  OnHoldReasonTooltip,
  StatusInfoTooltip,
  StatusWrapper,
} from 'shared/helpers/PaymentStatusHelpers';

const StyledTypographyTeal = styled(Typography)`
  color: ${ColorDynamic.Teal500};
`;

interface LoadPaymentStatusProps {
  payment?: LoadPaymentDTO | null;
  loadGUID: string;
  source: 'Load List' | 'Load Details';
}

function StatusDetails({ payment, loadGUID, source }: LoadPaymentStatusProps) {
  const { data: carrierSettings } = useCarrierSettings();
  const paymentError = carrierSettings?.superpay.payment_error;

  switch (payment?.status) {
    case 'processing':
      if (
        payment?.instant_transfer_fee_amount &&
        payment?.instant_transfer_available
      ) {
        return (
          <StatusWrapper
            icon={<CheckIcon fontSize="small" htmlColor={Color.Green300} />}
          >
            <TextBox color="green">{payment.status_details}</TextBox>
          </StatusWrapper>
        );
      }

      if (payment?.instant_transfer_fee_amount) {
        return (
          <StatusWrapper
            icon={
              <HourglassEmpty fontSize="small" htmlColor={Color.Green300} />
            }
          >
            <TextBox color="green">{payment.status_details}</TextBox>
          </StatusWrapper>
        );
      }

      if (payment?.instant_transfer_available) {
        return (
          <Box padding="xsmall" borderRadius="small" backgroundColor="Teal50">
            <StatusWrapper
              icon={
                <AccessTime fontSize="small" htmlColor={ColorDynamic.Teal500} />
              }
            >
              <StyledTypographyTeal>
                {payment.status_details}
              </StyledTypographyTeal>
            </StatusWrapper>
            <InstantTransferStatus
              loadGUID={loadGUID}
              source={source}
              buttonVariant="neutral"
            />
          </Box>
        );
      }

      return (
        <StatusWrapper
          icon={
            <AccessTime fontSize="small" htmlColor={ColorDynamic.Teal500} />
          }
        >
          <StyledTypographyTeal>
            {payment.status_details}
            {payment.status_info && <StatusInfoTooltip payment={payment} />}
          </StyledTypographyTeal>
        </StatusWrapper>
      );
    case 'received':
      return (
        <StatusWrapper
          icon={<CheckIcon fontSize="small" htmlColor={Color.Green300} />}
        >
          <TextBox color="green">{payment.status_details}</TextBox>
        </StatusWrapper>
      );
    case 'pending':
    case 'scheduled':
      return (
        <StatusWrapper icon={<AccessTime fontSize="small" color="primary" />}>
          <TextBox color="blue">
            {payment.status_details}
            {payment.status_info && <StatusInfoTooltip payment={payment} />}
          </TextBox>
        </StatusWrapper>
      );
    case 'past_due':
      return (
        <StatusWrapper
          icon={<WarningRounded fontSize="small" htmlColor={Color.Yellow500} />}
        >
          <TextBox color="yellow">
            {payment.status_details}
            {payment.status_info && <StatusInfoTooltip payment={payment} />}
          </TextBox>
        </StatusWrapper>
      );
    case 'payin_failed':
      if (payment.instant_transfer_fee_amount) {
        return (
          <StatusWrapper
            icon={
              <WarningRounded fontSize="small" htmlColor={Color.Yellow500} />
            }
          >
            <Stack space="xsmall">
              <TextBox color="red" variant="body-semibold">
                Shipper Payment Failed
              </TextBox>
              <TextBox color="yellow">
                {payment.status_details}
                {payment.status_info && <StatusInfoTooltip payment={payment} />}
              </TextBox>
            </Stack>
          </StatusWrapper>
        );
      }
      return (
        <StatusWrapper
          icon={<WarningRounded fontSize="small" htmlColor={Color.Yellow500} />}
        >
          <TextBox color="yellow">
            {payment.status_details}
            {payment.status_info && <StatusInfoTooltip payment={payment} />}
          </TextBox>
        </StatusWrapper>
      );
    case 'failed':
      return (
        <StatusWrapper
          icon={
            <IconTag
              display="inline"
              color="red"
              icon={<Info fontSize="small" color="error" />}
              align="center"
            >
              {payment.status_details}
            </IconTag>
          }
        >
          {paymentError && (
            <Tooltip
              interactive={true}
              title={
                <BankErrorMessage
                  paymentError={paymentError}
                  source="Load Tooltip"
                />
              }
            >
              <Info fontSize="small" color="action" />
            </Tooltip>
          )}
        </StatusWrapper>
      );
    case 'on_hold':
      return (
        <StatusWrapper
          icon={<WarningRounded fontSize="small" htmlColor={Color.Yellow300} />}
        >
          <TextBox color="yellow" variant="body-semibold">
            {payment.status_details}
            <OnHoldReasonTooltip payment={payment} />
          </TextBox>
        </StatusWrapper>
      );
    case 'canceled':
      return (
        <StatusWrapper
          icon={<WarningRounded fontSize="small" htmlColor={Color.Yellow500} />}
        >
          <TextBox color="yellow">
            {payment.status_details}
            {payment.status_info && <StatusInfoTooltip payment={payment} />}
          </TextBox>
        </StatusWrapper>
      );
    default:
      return null;
  }
}

export function LoadPaymentStatus({
  payment,
  loadGUID,
  source,
}: LoadPaymentStatusProps) {
  const isMobile = useResponsiveValue(true, false, false);

  if (payment?.status && payment.status_details) {
    return (
      <Stack space="xsmall">
        <StatusDetails payment={payment} loadGUID={loadGUID} source={source} />
        {payment.status !== 'received' && (
          <Typography
            variant={isMobile ? 'inherit' : 'body1'}
            color="textSecondary"
          >
            No Payment received
          </Typography>
        )}
      </Stack>
    );
  }

  if (payment?.paid_at) {
    return (
      <StatusWrapper
        icon={<CheckIcon fontSize="small" htmlColor={Color.Green300} />}
      >
        <TextBox color="green">
          Payment received on{' '}
          <FormattedDate date={payment.paid_at} variant="DateTime" />
        </TextBox>
      </StatusWrapper>
    );
  }

  return (
    <Typography variant={isMobile ? 'inherit' : 'body1'} color="textSecondary">
      No Payment received
    </Typography>
  );
}
